import * as os from 'os';
import * as path from 'path';
import * as YAML from 'yaml';
import * as utils from './utils';
import { promises as fsPromises } from 'fs';
import { TimeoutError } from './errors';
import { launchChrome } from './commons/chrome-launcher';
import * as config from './commons/config';
import type { AddressInfo } from 'net';
import type { Project } from 'clickim/src/app/services/projectsService';

export async function getProjectId() {
    return getCredentialProperty('projectId');
}

export async function getToken() {
    return getCredentialProperty('token');
}

function timeout<T>(promise: Promise<T>, ms: number) {
    // we need this to time out even if we disabled timeouts system wide
    return Promise.race([promise, utils.delay(ms).then(() => { throw new TimeoutError('timeout'); })]);
}

async function getCredentialsFromChrome() {
    const app = (await import('express')).default();
    const loginInfoFromChrome = (async function waitForChromeToSendData() {
        return timeout(new Promise<Project[]>(resolve => {
            app.get('/loginInfo', (req, res) => {
                resolve(JSON.parse(Buffer.from(req.query.info as string, 'base64').toString()));
                res.status(200).end();
            });
        }), 60000).catch(() => null);
    }());
    await new Promise((resolve, reject) => {
        // @ts-expect-error this doesn't behave as defined in types
        const server = app.listen(42543, (err) => {
            if (err) {
                reject(err);
            }
            resolve((server.address() as AddressInfo).port);
        });
    });

    try {
        const { getEditorUrl } = await import('./commons/testimServicesApi');
        const { editorUrl } = await getEditorUrl();
        launchChrome(`${editorUrl}/#/new-test`);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`Unable to open Testim automatically - please manually go to ${config.EDITOR_URL || config.DEFAULT_EDITOR_URL}`);
    }

    const data = await loginInfoFromChrome;
    return data;
}

export async function doLogin({ overwriteExisting = true, projects = null }: { overwriteExisting?: boolean; projects?: Project[] | null } = {}) {
    const homedir = os.homedir();

    const testimCredentialsFile = path.join(homedir, '.testim');

    const isExist = await utils.doesPathExist(testimCredentialsFile);

    if (isExist && !overwriteExisting) {
        return;
    }

    const credentials: { token?: string; projectId?: string } = {};

    const [{ default: prompts }, { default: ora }] = await Promise.all([import('prompts'), import('ora')]);

    const spinner = ora('Getting credentials from Tricentis Testim extension ...').start();

    if (!projects) {
        projects = await timeout(getCredentialsFromChrome(), 62000).catch(() => null);
    }

    // @ts-expect-error old version, can probably be removed safely
    if (projects?.token) { // V1(legacy) of the login extension API
        // @ts-expect-error old version, can probably be removed safely
        credentials.token = projects.token;
        // @ts-expect-error old version, can probably be removed safely
        credentials.projectId = projects.projectId;
        spinner.succeed();

        await writeCredentials(testimCredentialsFile, credentials);
        return;
    } if (projects?.length) { // V2(current) of the login extension API
        spinner.succeed();

        const response = projects.length === 1 ?
            { project: projects[0] } :
            await prompts({
                type: 'select',
                name: 'project',
                message: 'There are multiple projects associated with your user account. Please select the project you would like to connect to:',
                choices: projects.map(p => ({ title: p.name, value: p })),
            });

        credentials.token = response.project.ci.token;
        credentials.projectId = response.project.id;
        await writeCredentials(testimCredentialsFile, credentials);
        return;
    }

    spinner.fail();
    // eslint-disable-next-line no-console
    console.log('Error getting credentials - please pass `--token` and `--project` to the CLI or try again');
}

async function writeCredentials(testimCredentialsFile: string, credentials: { token?: string; projectId?: string }) {
    await fsPromises.writeFile(testimCredentialsFile, YAML.stringify(credentials));
    // eslint-disable-next-line no-console
    console.log(`Testim credentials saved in '${testimCredentialsFile}'`);
}

async function getCredentialProperty(property: 'projectId' | 'token') {
    const homedir = os.homedir();

    const testimCredentialsFile = path.join(homedir, '.testim');

    const isExist = await utils.doesPathExist(testimCredentialsFile);

    let credentials: { token?: string; projectId?: string } = {};

    if (isExist) {
        try {
            credentials = YAML.parse((await fsPromises.readFile(testimCredentialsFile)).toString());
        } catch (err) {
            // just use new credentials.
        }
    }

    // YAML.parse can return `null` or `undefined` if we fail with an empty/malformed file.
    credentials ||= {};

    return credentials[property];
}
